import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Upload from "./uploadSteps/Upload";
import EnrichPreview from "./uploadSteps/EnrichPreview";
import EnrichConfirmation from "./uploadSteps/EnrichConfirmation";
import DefMembershipActions from "../../Redux/actions/defMembership-actions";
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../Components/Loading";
import { stripBom, findPhoneColumnKey, normalizeImportRows, getPhoneFromRow } from "../../utils/importCsvUtils";

export const EnrichFileUpload = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const defMembership = useSelector((state) => state.defMembership.defMembership);

    const getMembership = useCallback(() => dispatch(DefMembershipActions.defMembershipRequest()), [dispatch]);
    const submitEnrichment = useCallback(
        (businessId, file) => dispatch(BusinessCustomersActions.customerEnrichmentUploadRequest(businessId, file)),
        [dispatch]
    );

    const [activeStep, setActiveStep] = useState(0);
    const [filteredRowsNoZero, setFilteredRowsNoZero] = useState([]);
    const [validFilteredRows, setValidFilteredRows] = useState([]);
    const [state, setState] = useState({
        file: {},
        uploading: false,
        uploadedEnrichment: null,
    });

    const isAdminOrOwner = defMembership?.role === "OWNER" || defMembership?.role === "ADMIN";

    useEffect(() => {
        if (!defMembership) {
            getMembership();
            return;
        }
        if (defMembership && !isAdminOrOwner) {
            navigate("/crm/customers", { replace: true });
        }
    }, [defMembership, getMembership, isAdminOrOwner, navigate]);

    const handleUpload = () => {
        const file = state.file;
        const reader = new FileReader();

        reader.onload = (event) => {
            let csvContent = event.target.result;
            if (typeof csvContent === "string") {
                csvContent = stripBom(csvContent);
            }
            const rows = csvContent.split("\n").filter((row) => row.trim() !== "");
            const headers = rows[0].split(",").map((header) => stripBom(header.trim()));
            const phoneColumnKey = findPhoneColumnKey(headers);
            const dataRows = rows.slice(1).map((row) => {
                const values = row.split(",");
                const rowObject = {};
                headers.forEach((header, index) => {
                    rowObject[header] = values[index]?.trim();
                });
                return rowObject;
            });
            const normalizedRows = normalizeImportRows(dataRows, phoneColumnKey);

            const startingWithZero = normalizedRows.filter((row) => {
                const phoneNumber = getPhoneFromRow(row, phoneColumnKey);
                return phoneNumber && phoneNumber.startsWith("0");
            });
            setValidFilteredRows(startingWithZero);
            setFilteredRowsNoZero(
                normalizedRows.filter((row) => {
                    const phoneNumber = getPhoneFromRow(row, phoneColumnKey);
                    return phoneNumber && !phoneNumber.startsWith("0");
                })
            );
        };

        reader.readAsText(file);
    };

    const handleContinue = () => {
        if (activeStep === 2) {
            setActiveStep(0);
            return;
        }
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setState((prev) => ({ ...prev, file: {} }));
        if (activeStep === 2) {
            setActiveStep(0);
            return;
        }
        setActiveStep((prev) => prev - 1);
    };

    const navigateToEnrichList = () => navigate("/crm/enrichment-requests");

    const handleSubmitEnrichment = (formData) => {
        submitEnrichment(defMembership.business.id, formData);
    };

    const steps = [
        { label: "business_customers.enrich_step_upload", icon: "pi-upload" },
        { label: "business_customers.enrich_step_validate", icon: "pi-search" },
        { label: "business_customers.enrich_step_success", icon: "pi-check-circle" },
    ];

    if (!defMembership || !isAdminOrOwner) {
        return <Loading />;
    }

    return (
        <div className="customerContent enrich-upload-screen">
            <div className="card card-div">
                <div className="fav-card-content">
                    <ul className="list-none p-0 m-0 flex flex-column md:flex-row ">
                        {steps.map(({ label, icon }, i) => (
                            <li key={i} className={`relative ${i + 1 !== steps.length ? "mr-0 md:mr-8" : ""}  flex-auto`}>
                                <div className={`step-box step-box-${activeStep === i ? "active" : i < activeStep ? "pre" : "post"}`}>
                                    <i className={`pi step-box-icon ${activeStep === i ? "step-box-active-icon " + icon : i < activeStep ? "step-box-pre-icon pi-check-circle" : "step-box-post-icon " + icon}`}></i>
                                    <div>
                                        <div className={`step-box-title step-box-${activeStep === i ? "active" : i < activeStep ? "pre" : "post"}-title`}>
                                            {`${i + 1 === steps.length ? t("totalTransfers.success") : t("totalTransfers.step", { val: i + 1 })}`}
                                        </div>
                                        <span className={`step-box-des step-box-${activeStep === i ? "active" : i < activeStep ? "pre" : "post"}-des  hidden md:block`}>
                                            {t(label)}
                                        </span>
                                    </div>
                                </div>
                                {i + 1 !== steps.length && <div className="hidden md:block step-box-bar"></div>}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div>
                {activeStep === 0 && (
                    <Upload
                        handleContinue={handleContinue}
                        handleUpload={handleUpload}
                        navigateToImportPage={navigateToEnrichList}
                        state={state}
                        setState={setState}
                        showAudienceContext={false}
                    />
                )}
                {activeStep === 1 && (
                    <EnrichPreview
                        handleBack={handleBack}
                        handleContinue={handleContinue}
                        navigateToImportPage={navigateToEnrichList}
                        validFilteredRows={validFilteredRows}
                        filteredRowsNoZero={filteredRowsNoZero}
                        onSubmitImport={handleSubmitEnrichment}
                        state={state}
                        setState={setState}
                    />
                )}
                {activeStep === 2 && (
                    <EnrichConfirmation
                        validFilteredRows={validFilteredRows}
                        navigateToEnrichList={navigateToEnrichList}
                        state={state}
                        setState={setState}
                        handleBack={handleBack}
                    />
                )}
            </div>
        </div>
    );
};
